import { generateID, asyncTimeout } from '@caresend/utils';
import { defineComponent, computed, ref, onUnmounted, watchEffect, watch, onMounted } from 'vue';
import __vue_component__ from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$4 from '../../transitions/TransitionTranslate/TransitionTranslate.vue.js';
import __vue_component__$1 from '../../ui/Icon.vue.js';
import __vue_component__$2 from '../../ui/InteractivePopup/components/ResizeObserver.vue.js';
import __vue_component__$3 from '../../ui/Tag/Tag.vue.js';

var script = defineComponent({
    name: 'Pane',
    components: {
        ButtonComponent: __vue_component__,
        Icon: __vue_component__$1,
        ResizeObserver: __vue_component__$2,
        Tag: __vue_component__$3,
        TransitionTranslate: __vue_component__$4,
    },
    model: {
        prop: 'isOpened',
        event: 'update',
    },
    props: {
        backgroundToDismiss: {
            type: Boolean,
            default: true,
        },
        confirmTo: {
            type: [String, Object],
            default: undefined,
        },
        debugMode: Boolean,
        transparentBackground: {
            type: Boolean,
            default: false,
        },
        hasFooter: {
            type: Boolean,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isOpened: Boolean,
        darkMode: Boolean,
        position: {
            type: String,
            default: 'bottom',
        },
        canCancel: {
            type: Boolean,
            default: true,
        },
        confirmDisabled: Boolean,
        displayFooter: {
            type: Boolean,
            default: true,
        },
        displayHeader: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: null,
        },
        subtitle: {
            type: String,
            default: null,
        },
        footerButtonText: {
            type: String,
            default: 'Confirm',
        },
        buttonStyle: {
            type: String,
            default: 'primary',
        },
    },
    emits: ['close', 'update', 'confirm', 'cancel'],
    setup(props, { emit }) {
        const classNames = computed(() => {
            const classes = [];
            if (props.position === 'left') {
                classes.push('Pane--left');
            }
            else if (props.position === 'right') {
                classes.push('Pane--right');
            }
            else if (props.position === 'bottom') {
                classes.push('Pane--bottom');
            }
            if (props.backgroundToDismiss) {
                if (props.position === 'left' || props.position === 'right') {
                    classes.push('Pane--full-width');
                }
                if (props.position === 'bottom') {
                    classes.push('Pane--full-height');
                }
            }
            if (props.transparentBackground) {
                classes.push('Pane--transparent-background');
            }
            return classes;
        });
        const bodyContainerClassNames = computed(() => {
            const classes = [];
            if (props.position === 'left' || props.position === 'right') {
                classes.push('Pane--body-container--horizontal');
            }
            else if (props.position === 'bottom') {
                classes.push('Pane--body-container--vertical');
            }
            return classes;
        });
        const bodyClassNames = computed(() => {
            const classes = [];
            if (props.darkMode) {
                classes.push('Pane--body-dark');
            }
            else {
                classes.push('Pane--body');
            }
            if (props.position === 'left') {
                classes.push('Pane--body-left');
            }
            else if (props.position === 'right') {
                classes.push('Pane--body-right');
            }
            else if (props.position === 'bottom') {
                classes.push('Pane--body-bottom');
            }
            return classes;
        });
        const isOpenedState = ref(false);
        /** TODO: Add bottom offset and padding for animation to have space for bounce */
        const defaultBottomOffset = 0;
        /**
         * The paneAgent is an optionally defined definition at whichever DOM position
         * you want this Pane to be mounted to. This is primarily helpful when you want
         * to mount this in a way that supercedes all other components.
         */
        const container = ref(null);
        const getOutCoordinates = () => {
            const curr = container.value;
            const width = (curr === null || curr === void 0 ? void 0 : curr.$el.clientWidth) || window.document.body.clientWidth;
            const height = (curr === null || curr === void 0 ? void 0 : curr.$el.clientHeight) || window.document.body.clientHeight;
            if (props.position === 'left') {
                return { x: -width, y: 0 };
            }
            if (props.position === 'right') {
                return { x: width, y: 0 };
            }
            if (props.position === 'bottom') {
                return { x: 0, y: height + defaultBottomOffset };
            }
            return { x: 0, y: 0 };
        };
        const translateX = ref(0);
        const translateY = ref(defaultBottomOffset);
        const appendToPaneAgent = () => {
            const paneAgent = document.getElementById('pane-agent');
            const curr = container.value;
            const element = curr === null || curr === void 0 ? void 0 : curr.$el;
            if (!paneAgent || !element)
                return;
            paneAgent.appendChild(element);
        };
        const localID = generateID();
        const removePaneAgentNodes = () => {
            const paneAgent = document.getElementById('pane-agent');
            paneAgent === null || paneAgent === void 0 ? void 0 : paneAgent.childNodes.forEach((child) => {
                var _a, _b;
                if (((_b = (_a = child).getAttribute) === null || _b === void 0 ? void 0 : _b.call(_a, 'data-id')) !== localID)
                    return;
                paneAgent.removeChild(child);
            });
        };
        onUnmounted(removePaneAgentNodes);
        watchEffect(async () => {
            if (props.isOpened) {
                isOpenedState.value = true;
                appendToPaneAgent();
                await asyncTimeout(10);
                translateX.value = 0;
                translateY.value = defaultBottomOffset;
            }
            else {
                const outCoordinates = getOutCoordinates();
                removePaneAgentNodes();
                translateX.value = outCoordinates.x;
                translateY.value = outCoordinates.y;
                await asyncTimeout(10);
                isOpenedState.value = false;
            }
        });
        watch(isOpenedState, () => {
            emit('update', isOpenedState.value);
            if (!isOpenedState.value)
                emit('close');
        });
        const emitCancel = () => { emit('cancel'); };
        const closeModal = async (cancel = true) => {
            const outCoordinates = getOutCoordinates();
            translateX.value = outCoordinates.x;
            translateY.value = outCoordinates.y;
            await asyncTimeout(50);
            if (cancel)
                emitCancel();
            isOpenedState.value = false;
        };
        const emitConfirm = async () => {
            emit('confirm');
            await closeModal(false);
        };
        const containerWidth = ref(0);
        const containerHeight = ref(0);
        const dimensionsHandler = (dimensions) => {
            containerWidth.value = dimensions.width;
            containerHeight.value = dimensions.height;
        };
        const resizeHandler = () => {
            const curr = container.value;
            dimensionsHandler({
                width: (curr === null || curr === void 0 ? void 0 : curr.$el.clientWidth) || 0,
                height: (curr === null || curr === void 0 ? void 0 : curr.$el.clientHeight) || 0,
            });
        };
        onMounted(() => {
            window.document.addEventListener('resize', resizeHandler);
        });
        const contentDimensionsHandler = () => {
            resizeHandler();
        };
        return {
            bodyClassNames,
            bodyContainerClassNames,
            classNames,
            closeModal,
            container,
            contentDimensionsHandler,
            dimensionsHandler,
            emitCancel,
            emitConfirm,
            localID,
            isOpenedState,
            translateX,
            translateY,
        };
    },
});

export { script as default };
